/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone} from '@angular/core';
import template from './role-edit-page.component.html';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import * as _ from 'lodash';

import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {PERMISSIONS} from '../../../components/auth/auth-constants';
import {
  buildRolePermissionConfig,
  cleanupPermissionsOnSubmit,
  RolePermissionConfig,
  setupRoleModel
} from './role-commons';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';

@Component({
  standalone: false,
  selector: 'adn-role-edit-page',
  template
})
export class RoleEditPageComponent extends AdminResourceEditPageBase {
  private readonly specialUserOnlyPermissions = new Set([
    'AX_PUBLISHER_TEAM_ADMIN',
    'AX_ADVERTISER_TEAM_ADMIN'
  ]);
  readonly roleConfig: RolePermissionConfig;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private localUserProfile: any,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.roleConfig = buildRolePermissionConfig(LocalNetworkProfile);
  }

  get showScopeAndPermissions(): boolean {
    return this.editingModel?.application === 'ADVERTISING'
      || this.editingModel?.application === 'DATA'
      || this.editingModel?.application === 'POLITICAL';
  }

  get visiblePermissions(): any[] {
    if (!this.editingModel?.scope) {
      return [];
    }
    const fromConfig = (this.roleConfig.permissionByType[this.editingModel.roleType] || [])
      .filter((permission) => permission.application === this.editingModel.application
        && permission.scope === this.editingModel.scope);
    const inListKeys = new Set(fromConfig.map((p) => p.apiKey));
    const assignedExtras = (this.editingModel.permissions || [])
      .filter((apiKey: string) => !inListKeys.has(apiKey))
      .map((apiKey: string) => _.find(PERMISSIONS, ['apiKey', apiKey]))
      .filter((p: any) => p
        && p.application === this.editingModel.application
        && p.scope === this.editingModel.scope
        && (!p.roleType || p.roleType === this.editingModel.roleType));
    // this is temporary until we properly release the mp team admin permissions work
    return [...fromConfig, ...assignedExtras]
      .filter((permission) => this.canShowPermission(permission.apiKey));
  }

  canShowPermission(apiKey: string): boolean {
    return !this.specialUserOnlyPermissions.has(apiKey) || this.localUserProfile.isSpecialUser();
  }

  hasApplication(application: string): boolean {
    return this.roleConfig.applications.indexOf(application) > -1;
  }

  permissionLabel(apiKey: string): string {
    return 'permissions.' + apiKey;
  }

  permissionDescription(apiKey: string): string {
    return 'permissions.' + apiKey + '.desc';
  }

  isPermissionSelected(apiKey: string): boolean {
    return (this.editingModel.permissions || []).includes(apiKey);
  }

  onPermissionChange(apiKey: string, event: Event): void {
    this.togglePermission(apiKey, (event.target as HTMLInputElement).checked);
  }

  togglePermission(apiKey: string, checked: boolean): void {
    let permissions = [...(this.editingModel.permissions || [])];
    if (checked && !permissions.includes(apiKey)) {
      permissions.push(apiKey);
    }
    if (!checked) {
      permissions = permissions.filter((entry) => entry !== apiKey);
    }
    this.editingModel.permissions = permissions;
    this.markExternalFormDirty();
  }

  protected afterInitEditingModel(): void {
    setupRoleModel(this.editingModel, this.roleConfig.applications, this.localUserProfile);
  }

  protected afterInitAfterSave(): void {
    setupRoleModel(this.editingModel, this.roleConfig.applications, this.localUserProfile);
  }

  protected onBeforeSubmit(): void {
    cleanupPermissionsOnSubmit(this.editingModel);
  }
}
